import React, { useEffect, useRef, useContext } from "react";
import { useLocation, useHistory } from "react-router-dom";

import Section from "../../baseComponents/Section";
import Attachments, { IAttachmentEntryFormRef } from "../Attachments";
import SLCreateComments, { ISLCreateCommentFormRef } from "./SLCreateComments";
import SLCreateHeader, { ISLCreateHeaderRef } from "./SLCreateHeader";
import SLCreateLineItems, { ISLCreateLineItemsGridRef } from "./SLCreateLineItems/SLCreateLineItems";
import SLCreatePostersReviewers, { ISLCreatePostersReviewersRef } from "./SLCreatePostersReviewers";
import PageHeading from "../../baseComponents/PageHeading";
import { assembleSLEntry, saveEntry, postEntry, validate, sendToPoster } from "./SLCreateActionsRequests";
import { SLEntriesError, RuntimeError, FatalError } from "../../services/ErrorHelpers";
import { IValidationMessage } from "../../schemas/ISLEntryResponse";
import { getStateObjects, redirectMeToTheOrigin } from "../../services/UrlHelpers";
import SLBatchPopupOverlay, { ISLBatchPopupOverlay } from "../SLBatchPopup/SLBatchPopup";
import SLActionsManager, { ISLActionsManager } from "../SLActionsManager/SLActionsManager";
import { IMessages } from "../SLActionsManager/SLActions";
import { slEntryResponseToSlEntryGridItem } from "./utilities";
import SLCreateAuditInfo from "./SLCreateAuditInfo";
import { UserContext, IUserProviderState } from "../../contexts/UserContext";
import { SubledgerContext, SubledgerContextData } from "../../contexts/SubledgerContext";
import { LoggingContext } from "../../contexts/LoggingContext";
import SLCreateActionBar, { SLCreateActionBarRef, AllSLCreateActions } from "./SLCreateActionBar";
import SLCreateMetadata from "./SLCreateMetadata";
import { getEntryFromApi } from "./SLCreateGetAndSanitization";
import { SLCreateSLBatchProvider } from "../../contexts/SLCreateSLBatchContext";
import { SLLoadingStatus, delay } from "../../utilities/utilities";
import { ISLEntryResponse } from "./SLCreateSchemas";
import { useDataFromSubledger } from "../../hooks/useDataFromSubledger";

interface BaseCommandOptions {
  redirectToDashboard: boolean;
}

const BaseCommandsDefaults: BaseCommandOptions = {
  redirectToDashboard: false
};

function baseCommandBuilder(
  onNotification: SLCreateActionBarRef["setMessage"],
  resetNotifications: SLCreateActionBarRef["reset"],
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>
) {
  const baseCommand = async (
    commandNameStr: string,
    callback: () => Promise<string[]>,
    opts: BaseCommandOptions = BaseCommandsDefaults
  ) => {
    resetNotifications();
    await delay(200);
    onNotification("info", [`${commandNameStr} is pending.`]);
    try {
      // do command here
      const messages = await callback();
      resetNotifications();
      await delay(200);
      onNotification("success", messages);
      if (opts.redirectToDashboard && messages.length > 0) {
        redirectMeToTheOrigin(history, location);
      }
    } catch (e) {
      // handle errors here
      resetNotifications();
      await delay(200);
      if (e instanceof SLEntriesError) {
        onNotification("errors", e.messages);
      } else {
        onNotification("errors", [`${commandNameStr} failed.`, (e as unknown as Error).message]);
      }
    }
  };
  return baseCommand;
}

function CommandHandler(
  onNotification: SLCreateActionBarRef["setMessage"],
  resetNotifications: SLCreateActionBarRef["reset"],
  history: any,
  location: any,
  entryId: number,
  userRoles: SubledgerContextData["userRoles"],
  accessToken: IUserProviderState["accessToken"],
  slActionsManagerRef: React.RefObject<ISLActionsManager["ref"]>,
  headerRef: React.RefObject<ISLCreateHeaderRef>,
  lineItemsRef: React.RefObject<ISLCreateLineItemsGridRef>,
  commentsRef: React.RefObject<ISLCreateCommentFormRef>,
  attachmentsRef: React.RefObject<IAttachmentEntryFormRef>,
  postersRef: React.RefObject<ISLCreatePostersReviewersRef>,
  setEntry: ReturnType<typeof useDataFromSubledger>[1]
) {
  const onCommand = async (commandName: AllSLCreateActions) => {
    const exceptionHandler = baseCommandBuilder(onNotification, resetNotifications, history, location);
    const saveAndLoad = async () => {
      const fields = assembleSLEntry(headerRef, lineItemsRef, commentsRef, attachmentsRef, postersRef);
      const payload = await saveEntry(entryId, fields.entry, accessToken);
      setEntry(payload.entry);
      if (location.pathname === "/slcreate") {
        const st = getStateObjects(location);
        history.push(`/slcreate/${payload.entry.slEntryId}`, st ? st.state : undefined);
      }
      return {
        ...payload
      };
    };

    switch (commandName) {
      case "save":
        exceptionHandler("Save", async () => {
          const payload = await saveAndLoad();
          return payload.messages;
        });
        break;
      case "post":
        exceptionHandler(
          "Post",
          async () => {
            const payload = await saveAndLoad();
            const result = await postEntry(payload.entry.slEntryId as number, accessToken);
            if (!result) {
              throw new RuntimeError("No result received from API.");
            }
            const apiMessages = (result.data as IValidationMessage[]).map((item) => item.errorMessage);
            // if result.data.length is a 0 then there was no validation error so posted successfully
            // Yes, same error payload of IValidationMessage is returned when successful
            if (apiMessages.every((message) => message === null)) {
              return [`Successfully posted entry. Entry ID is ${payload.entry.slEntryId}.`];
            }
            throw new SLEntriesError([...apiMessages]);
          },
          { redirectToDashboard: true }
        );
        break;
      case "sendToPoster":
        exceptionHandler(
          "Send to Poster",
          async () => {
            const payload = await saveAndLoad();
            // check for empty additionalPosters
            if (!payload.entry.additionalPoster) {
              throw new SLEntriesError(["Poster cannot be empty. Please enter a valid Email Name in poster field"]);
            }
            const result = await sendToPoster(payload.entry.slEntryId as number, accessToken);
            return result.messages;
          },
          { redirectToDashboard: true }
        );
        break;
      case "validate":
        exceptionHandler("Validate", async () => {
          if (!userRoles.value.isReviewer) {
            // Validate disabling is handled by rowWeightage
            const payload = await saveAndLoad();
            const result = await validate(payload.entry.slEntryId as number, accessToken);
            return result.messages;
          } else {
            const result = await validate(entryId, accessToken);
            return result.messages;
          }
        });
        break;
      case "clearForm":
        resetNotifications();
        setEntry(null);
        break;
      case "approve":
        exceptionHandler(
          "Approve",
          async () => {
            const slEntryFromApi = await getEntryFromApi(entryId, accessToken);
            const approveHandler = (): Promise<IMessages | null> => {
              return new Promise((resolve, reject) => {
                if (slActionsManagerRef.current) {
                  const onSubmitSubject = slActionsManagerRef.current.getOnSubmitAsyncSubject();
                  onSubmitSubject.subscribe(
                    (results) => {
                      resolve(results);
                    },
                    (e) => {
                      reject(e);
                    }
                  );
                  slActionsManagerRef.current.open("ApproveEntries", [
                    slEntryResponseToSlEntryGridItem(slEntryFromApi)
                  ]);
                }
              });
            };
            const result = await approveHandler();
            if (result) {
              return result.messages;
            } else {
              return [];
            }
          },
          { redirectToDashboard: true }
        );
        break;
      case "needsClarification":
        exceptionHandler(
          "Needs Clarification",
          async () => {
            const slEntryFromApi = await getEntryFromApi(entryId, accessToken);
            const needsClarificationHandler = (): Promise<IMessages | null> => {
              return new Promise((resolve, reject) => {
                if (slActionsManagerRef.current) {
                  const onSubmitSubject = slActionsManagerRef.current.getOnSubmitAsyncSubject();
                  onSubmitSubject.subscribe(
                    (results) => {
                      resolve(results);
                    },
                    (e) => {
                      reject(e);
                    }
                  );
                  slActionsManagerRef.current.open("NeedClarification", [
                    slEntryResponseToSlEntryGridItem(slEntryFromApi)
                  ]);
                }
              });
            };
            const result = await needsClarificationHandler();
            if (result) {
              return result.messages;
            } else {
              return [];
            }
          },
          { redirectToDashboard: true }
        );
        break;
      case "recall":
        exceptionHandler(
          "Recall",
          async () => {
            const slEntryFromApi = await getEntryFromApi(entryId, accessToken);
            const recallHandler = (): Promise<IMessages | null> => {
              return new Promise((resolve, reject) => {
                if (slActionsManagerRef.current) {
                  const onSubmitSubject = slActionsManagerRef.current.getOnSubmitAsyncSubject();
                  onSubmitSubject.subscribe(
                    (results) => {
                      resolve(results);
                    },
                    (e) => {
                      reject(e);
                    }
                  );
                  slActionsManagerRef.current.open("RecallEntries", [slEntryResponseToSlEntryGridItem(slEntryFromApi)]);
                }
              });
            };
            const result = await recallHandler();
            if (result) {
              return result.messages;
            } else {
              return [];
            }
          },
          { redirectToDashboard: true }
        );
        break;
    }
  };

  return onCommand;
}

const SLCreate: React.FC = () => {
  const { appInsights } = useContext(LoggingContext);
  if (!appInsights) {
    throw new FatalError("Please setup ApplicationInsights Provider for SLCreate");
  }
  const { accessToken } = useContext(UserContext);
  const { userRoles } = useContext(SubledgerContext);
  const [endpointData, setEntry] = useDataFromSubledger("SLCreate");

  // hear ye human: this component rules all of slcreate
  // using useState on this component would be a super expensive operation to track down
  // because it needs to re render so much
  // for the actions all we need is the entry id, the data comes from subcomponents of slcreate
  // and so we use a ref, to avoid triggering any re renderings we a variable changes
  // do not use useState in this component unless you want to be in a world of pain
  const entryId = useRef(0);

  const history = useHistory();
  const location = useLocation();

  const slActionsManagerRef = useRef<ISLActionsManager["ref"]>(null);
  const slBatchPopupRef = useRef<ISLBatchPopupOverlay["ref"]>(null);
  const _notificationsRef = useRef<SLCreateActionBarRef>(null);

  const headerRef = useRef<ISLCreateHeaderRef>(null);
  const lineItemsRef = useRef<ISLCreateLineItemsGridRef>(null);
  const commentsRef = useRef<ISLCreateCommentFormRef>(null);
  const attachmentsRef = useRef<IAttachmentEntryFormRef>(null);
  const postersRef = useRef<ISLCreatePostersReviewersRef>(null);

  const onNotification: SLCreateActionBarRef["setMessage"] = (type, messages) => {
    if (_notificationsRef.current) {
      _notificationsRef.current.setMessage(type, messages);
    }
  };

  const resetNotifications: SLCreateActionBarRef["reset"] = () => {
    if (_notificationsRef.current) {
      _notificationsRef.current.reset();
    }
  };

  useEffect(() => {
    if (endpointData.loadingStatus === SLLoadingStatus.Resolved) {
      if (endpointData.entry === null) {
        resetNotifications();
      }
    } else if (endpointData.loadingStatus === SLLoadingStatus.Rejected) {
      if (endpointData.notification) {
        onNotification(endpointData.notification.type, endpointData.notification.messages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointData.loadingStatus]);

  useEffect(() => {
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (endpointData.entry !== null) {
      const entry = endpointData.entry as ISLEntryResponse;
      entryId.current = entry.slEntryId;
    } else {
      entryId.current = 0;
    }
  }, [endpointData.entry]);

  return (
    <SLCreateSLBatchProvider endpointData={endpointData}>
      <SLBatchPopupOverlay ref={slBatchPopupRef} />
      <SLActionsManager
        ref={slActionsManagerRef}
        onNotifications={onNotification}
        batchPopup={slBatchPopupRef.current}
      />
      <div className="content">
        <PageHeading onClose={() => redirectMeToTheOrigin(history, location, 0)}>Subledger Entry</PageHeading>
        <SLCreateActionBar
          ref={_notificationsRef}
          onCommand={(commandName) => {
            const commands = CommandHandler(
              onNotification,
              resetNotifications,
              history,
              location,
              entryId.current,
              userRoles,
              accessToken,
              slActionsManagerRef,
              headerRef,
              lineItemsRef,
              commentsRef,
              attachmentsRef,
              postersRef,
              setEntry
            );
            commands(commandName);
          }}
        >
          <SLCreateMetadata />
          <Section label="Header">
            <SLCreateHeader ref={headerRef} />
          </Section>
          <Section label="Attachments">
            <Attachments ref={attachmentsRef} onNotification={onNotification} />
          </Section>
          <Section label="Line Items">
            <SLCreateLineItems ref={lineItemsRef} />
          </Section>
          <Section label="Comments">
            <SLCreateComments ref={commentsRef} />
          </Section>
          <Section label="Posters and Reviewers">
            <div className="ms-Grid" dir="ltr">
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-lg6">
                  <SLCreatePostersReviewers ref={postersRef} />
                </div>
                <div className="ms-Grid-col ms-lg6">
                  <SLCreateAuditInfo />
                </div>
              </div>
            </div>
          </Section>
        </SLCreateActionBar>
      </div>
    </SLCreateSLBatchProvider>
  );
};

SLCreate.displayName = "SLCreate";

export default SLCreate;
